import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'clsx';
import styles from './style.module.scss';
import ChipList from '@components-clean/core/chip-list';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import IHubDataset from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import { DatasetLogs } from '@pages-clean/central-monitoring/dataset-hub/tabs/dataset-info/dataset-logs';
import DatasetNotes, {
    COMPONENT_NAME as DATASET_NOTES_COMPONENT_NAME,
} from '@pages-clean/central-monitoring/dataset-hub/tabs/dataset-info/dataset-notes';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

type Props = {
    studyRef: string;
    dataset?: IHubDataset;
};

enum ChipValues {
    Notes = 1,
    Logs = 2,
}

const DatasetInfo = ({ studyRef, dataset }: Props) => {
    const { t } = useTranslation('cm', {
        keyPrefix: 'datasetHub.tabs.datasetInfo',
    });
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    const [chip, setChip] = useState<ChipValues>(ChipValues.Notes);

    const unsavedChanges = componentHasChanges(DATASET_NOTES_COMPONENT_NAME);
    const titleComponent = useMemo(
        () => (
            <div className={cn('k-d-flex', styles.datasetInfoCardTitle)}>
                <div>{`${t('cardTitle')} ${stringDateWithoutTimeFromUtc(dataset?.date, 'DD-MMM-YYYY') ?? ''}`}</div>
                <ChipList
                    // We use a compound key here to prevent a bug with the focus state of the chiplist causing
                    // some rerenders to steal focus from input elements
                    key={String(componentHasChanges(DATASET_NOTES_COMPONENT_NAME))}
                    data-testid="dataset-info-chips"
                    defaultData={[
                        { text: t('chips.datasetNotes.title'), value: ChipValues.Notes },
                        { text: t('chips.datasetLogs.title'), value: ChipValues.Logs },
                    ]}
                    value={chip}
                    onChange={async (event) => {
                        if (event.value == null) return;
                        if (chip === ChipValues.Notes && componentHasChanges(DATASET_NOTES_COMPONENT_NAME)) {
                            const confirmed = await confirmDiscardChanges();
                            if (!confirmed) return;
                            setComponentHasChanges(DATASET_NOTES_COMPONENT_NAME, false);
                        }
                        setChip(event.value as ChipValues);
                    }}
                    selection="single"
                />
            </div>
        ),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [chip, dataset?.date, unsavedChanges],
    );

    if (chip === ChipValues.Notes)
        return <DatasetNotes studyRef={studyRef} dataset={dataset} cardTitle={titleComponent} />;

    return <DatasetLogs studyRef={studyRef} dataset={dataset} cardTitle={titleComponent} />;
};

export default DatasetInfo;
